import { useUserInfo } from "@/api/user";
import { PERMISSIONS } from "@/constants/permissions";
import { ROUTES } from "@/constants/routes";
import { usePermissions } from "@/hooks/usePermissions";
import { Flags, flagValues } from "@/providers/LaunchDarklyProvider";
import { clearSessionQueryCaches } from "@/queryPersistence";
import { useAuth0 } from "@auth0/auth0-react";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  Archive,
  ArrowLeftRight,
  Blocks,
  Building2,
  CreditCard,
  Download,
  FileText,
  Frame,
  Gauge,
  Group,
  Layers,
  LogOut,
  MessagesSquare,
  Mic,
  Plug,
  PlugZap,
  Plus,
  ScrollText,
  SlidersHorizontal,
  SunMoon,
  UserRound,
  UserRoundPlus,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import { useMemo } from "react";
import type { ManageRail, ManageRailAction, ManageTab } from "./ManageModal";

/**
 * The Manage modal's configuration — which tabs exist, which scopes each rail
 * carries, which permission/flag/plan gates hide them, and which deep-link
 * path lands where. This is the successor to the old admin-panel navigation
 * registry: one list, gated once, feeding both the shell and the route sync.
 *
 * Entry values are paths (that is what `useOpenSettings` takes), so the shell's
 * alias/landing maps are keyed by path — a deep link opens the tab it belongs
 * to, landed on its scope, without any entry point reaching into the rail.
 */

/** A rail row. `id` doubles as the scope key and the row's visible label. */
type ScopeDef = {
  id: string;
  icon: LucideIcon;
  /** Rail section head this row sits under. */
  group?: string;
  /** Deep-link path that lands on this scope. */
  path?: string;
  /** Retired paths that still land here (old links, checkout round-trips). */
  legacyPaths?: string[];
  requiredPermissions?: string[];
  requiredFlags?: string[];
  requiredPlans?: string[];
};

type TabDef = {
  id: string;
  label: string;
  /** Deep-link path for the tab itself; lands on its first visible scope. */
  path: string;
  legacyPaths?: string[];
  scopes: ScopeDef[];
};

const ORG_ADMIN = [PERMISSIONS.ORG_ADMIN.ALL];

const TAB_DEFS: TabDef[] = [
  {
    id: "settings",
    label: "Settings",
    path: ROUTES.SETTINGS.PROFILE,
    legacyPaths: [ROUTES.SETTINGS.MY_SETTINGS],
    scopes: [
      {
        id: "Profile",
        icon: UserRound,
        group: "Account",
        path: ROUTES.SETTINGS.PROFILE,
        legacyPaths: [ROUTES.SETTINGS.MY_SETTINGS],
      },
      {
        id: "Organization",
        icon: Building2,
        group: "Workspace",
        path: ROUTES.SETTINGS.ORGANIZATION,
        // Single sign-on folded into the Organization scope as a section.
        legacyPaths: [ROUTES.SETTINGS.IDENTITY_PROVISIONING],
        requiredPermissions: ORG_ADMIN,
      },
      {
        id: "Members",
        icon: UsersRound,
        group: "Workspace",
        path: ROUTES.SETTINGS.MEMBERS,
        legacyPaths: [ROUTES.SETTINGS.USERS],
        requiredPermissions: ORG_ADMIN,
      },
      {
        id: "Groups",
        icon: Group,
        group: "Workspace",
        path: ROUTES.SETTINGS.GROUPS,
        requiredPermissions: ORG_ADMIN,
      },
      // Preferences is a group, not a page: its scopes stack into one
      // scrolling pane and the rail runs as that page's table of contents.
      {
        id: "Appearance",
        icon: SunMoon,
        group: "Preferences",
        path: ROUTES.SETTINGS.PREFERENCES,
      },
      {
        id: "Audio",
        icon: Mic,
        group: "Preferences",
        legacyPaths: [ROUTES.SETTINGS.AUDIO],
        requiredFlags: [flagValues.audioTranscription],
      },
      { id: "System", icon: SlidersHorizontal, group: "Preferences" },
    ],
  },
  {
    id: "usage",
    label: "Usage",
    path: ROUTES.SETTINGS.USAGE,
    scopes: [
      {
        id: "Overview",
        icon: Gauge,
        group: "Usage",
        path: ROUTES.SETTINGS.USAGE,
        requiredPermissions: ORG_ADMIN,
        requiredFlags: [flagValues.billing],
      },
      {
        id: "Billing",
        icon: CreditCard,
        group: "Usage",
        path: ROUTES.SETTINGS.USAGE_BILLING,
        legacyPaths: [ROUTES.SETTINGS.USAGE_BILLING_LEGACY],
        requiredPermissions: ORG_ADMIN,
        requiredFlags: [flagValues.billing],
      },
    ],
  },
  {
    id: "connectors",
    label: "Connectors",
    path: ROUTES.SETTINGS.CONNECTORS,
    scopes: [
      {
        id: "All",
        icon: Blocks,
        group: "Directory",
        path: ROUTES.SETTINGS.CONNECTORS,
      },
      { id: "Connected", icon: PlugZap, group: "Directory" },
      { id: "Available", icon: Plug, group: "Directory" },
    ],
  },
  {
    id: "archive",
    label: "Archive",
    path: ROUTES.SETTINGS.ARCHIVE,
    scopes: [
      {
        id: "All",
        icon: Archive,
        group: "Archive",
        path: ROUTES.SETTINGS.ARCHIVE,
      },
      { id: "Canvases", icon: Frame, group: "Archive" },
      { id: "Files", icon: FileText, group: "Archive" },
      { id: "Chats", icon: MessagesSquare, group: "Archive" },
      { id: "Skills", icon: ScrollText, group: "Archive" },
      { id: "Workspaces", icon: Layers, group: "Archive" },
    ],
  },
];

export type ManageModalConfig = {
  tabs: ManageTab[];
  rails: Record<string, ManageRail>;
  aliases: Record<string, string>;
  landing: Record<string, string>;
  /** Every visible deep-link path — the route sync's permission source. */
  allMenuItems: { path: string }[];
  /**
   * Where a bare `openSettings()` (or an unresolvable path) lands. The design
   * system's contract is that the entry point picks the landing — so this is
   * Settings·Profile, never "whichever tab happens to be first".
   */
  defaultTab: string | undefined;
};

export function useManageModalConfig(
  options: {
    /** Opens the seat picker — the same one Members' capacity prompt uses. */
    onAddSeats?: () => void;
  } = {},
): ManageModalConfig {
  const { onAddSeats } = options;
  const flags = useFlags<Flags>();
  const { data: userInfo } = useUserInfo();
  const userPermissions = usePermissions();
  const { loginWithRedirect, logout } = useAuth0();

  const planName = userInfo?.subscription?.plan_name;
  const permissionsKey = userPermissions.join(",");
  const isMultiOrg = (userInfo?.organizations?.length ?? 0) > 1;

  return useMemo(() => {
    const railActions = (tabId: string): ManageRailAction[] | undefined => {
      if (tabId === "connectors") {
        return [
          {
            icon: Plus,
            label: "Add connector",
            tint: "neutral",
            scope: "Available",
          },
        ];
      }
      if (tabId === "settings") {
        const actions: ManageRailAction[] = [];
        if (userPermissions.includes(PERMISSIONS.ORG_ADMIN.ALL)) {
          actions.push({
            icon: UserRoundPlus,
            label: "Invite members",
            tint: "neutral",
            scope: "Members",
          });
        }
        if (isMultiOrg) {
          actions.push({
            icon: ArrowLeftRight,
            label: "Switch organization",
            tint: "deep",
            onSelect: () =>
              loginWithRedirect({
                authorizationParams: {
                  prompt: "select_account",
                  ignoreCache: true,
                },
              }),
          });
        }
        // Log out sits at the foot of the settings column — the account-level
        // verb under the account-level rail, in the same red the account menu
        // uses. It is the one action here that isn't gated: everyone can
        // leave.
        actions.push({
          icon: LogOut,
          label: "Log out",
          tint: "danger",
          onSelect: () => {
            clearSessionQueryCaches();
            logout({ logoutParams: { returnTo: window.location.origin } });
          },
        });
        return actions;
      }
      if (tabId === "usage") {
        // The two follow-ups a plan page invites. Seats are the standing verb
        // here — Members only offers them once you're already full, which is
        // the moment you can no longer act on them calmly. Download invoices
        // just walks to the scope that holds them.
        return [
          {
            icon: Plus,
            label: "Add seats",
            tint: "neutral",
            onSelect: onAddSeats,
          },
          {
            icon: Download,
            label: "Download invoices",
            tint: "deep",
            scope: "Billing",
          },
        ];
      }
      // Archive's verbs belong to its rows (an "empty the archive" button is a
      // foot-gun, not a shortcut).
      return undefined;
    };

    const visible = (scope: ScopeDef) => {
      if (
        scope.requiredPermissions?.some((p) => !userPermissions.includes(p))
      ) {
        return false;
      }
      if (scope.requiredFlags?.some((f) => !flags[f as keyof Flags])) {
        return false;
      }
      if (
        scope.requiredPlans &&
        scope.requiredPlans.length > 0 &&
        !scope.requiredPlans.some((plan) => planName === plan)
      ) {
        return false;
      }
      return true;
    };

    const tabs: ManageTab[] = [];
    const rails: Record<string, ManageRail> = {};
    const aliases: Record<string, string> = {};
    const landing: Record<string, string> = {};
    const allMenuItems: { path: string }[] = [];

    for (const tab of TAB_DEFS) {
      const scopes = tab.scopes.filter(visible);
      if (scopes.length === 0) continue;

      tabs.push({ id: tab.id, label: tab.label });

      // Rail sections, in declaration order, grouped by head.
      const sections: ManageRail["sections"] = [];
      for (const scope of scopes) {
        const head = scope.group;
        const last = sections[sections.length - 1];
        if (last && last.head === head) {
          last.items.push({ id: scope.id, icon: scope.icon });
        } else {
          sections.push({ head, items: [{ id: scope.id, icon: scope.icon }] });
        }
      }
      rails[tab.id] = { sections, actions: railActions(tab.id) };

      // The tab's own default landing is its first visible scope.
      landing[tab.id] = scopes[0].id;

      const register = (path: string, scopeId: string) => {
        aliases[path] = tab.id;
        landing[path] = scopeId;
        allMenuItems.push({ path });
      };

      // The tab path lands on the first visible scope even when the declared
      // path belongs to a scope that is gated away.
      register(tab.path, scopes[0].id);
      for (const legacy of tab.legacyPaths ?? []) {
        register(legacy, scopes[0].id);
      }
      for (const scope of scopes) {
        if (scope.path) register(scope.path, scope.id);
        for (const legacy of scope.legacyPaths ?? []) {
          register(legacy, scope.id);
        }
      }
    }

    return {
      tabs,
      rails,
      aliases,
      landing,
      allMenuItems,
      // Prefer Settings·Profile; fall back to the first visible tab only when
      // Settings is entirely gated away.
      defaultTab: aliases[ROUTES.SETTINGS.PROFILE]
        ? ROUTES.SETTINGS.PROFILE
        : tabs[0]?.id,
    };
    // `userPermissions` is a fresh array each render; key on its contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    permissionsKey,
    flags,
    planName,
    isMultiOrg,
    loginWithRedirect,
    onAddSeats,
  ]);
}
